import axios from 'axios';

const api = axios.create({
  baseURL: '/api/proxy',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Read env from the current URL to forward to the proxy (e.g., env=dev)
const envFromUrl = new URLSearchParams(window.location.search).get('env');

api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

const fetchQuestions = async (
  language,
  clientId,
  version,
  type,
  clinicId,
  groupId
) => {
  try {
    let url = '/?route=questions&lang=' + language;
    if (clientId) {
      url += '&clientId=' + clientId;
    }
    if (version) {
      url += '&version=' + version;
    }
    if (type) {
      url += '&type=' + type;
    }
    if (clinicId) {
      url += '&clinicId=' + clinicId;
    }
    if (groupId) {
      url += '&groupId=' + groupId;
    }
    if (envFromUrl) {
      url += '&env=' + envFromUrl;
    }
    const response = await api.get(url);
    return response;
  } catch (error) {
    console.error('Failed to fetch questions:', error);
    throw error;
  }
};

const fetchScores = async (
  data,
  language,
  clientId,
  professionalId,
  version,
  questionnaireId,
  type,
  clinicId,
  groupId
) => {
  try {
    let url = '/?route=scores&lang=' + language;
    if (clientId) {
      url += '&clientId=' + clientId;
    }
    if (professionalId) {
      url += '&professionalId=' + professionalId;
    }
    if (version) {
      url += '&version=' + version;
    }
    if (questionnaireId) {
      url += '&questionnaireId=' + questionnaireId;
    }
    if (type) {
      url += '&type=' + type;
    }
    if (clinicId) {
      url += '&clinicId=' + clinicId;
    }
    if (groupId) {
      url += '&groupId=' + groupId;
    }
    if (envFromUrl) {
      url += '&env=' + envFromUrl;
    }
    const response = await api.post(url, data);
    return response;
  } catch (error) {
    console.error('Failed to submit scores:', error);
    throw error;
  }
};

const saveQuestionnaire = async (
  data,
  clientId,
  version,
  type,
  clinicId,
  groupId
) => {
  try {
    let url = '/?route=save-questionnaire&version=' + version;
    if (clientId) {
      url += '&clientId=' + clientId;
    }
    if (type) {
      url += '&type=' + type;
    }
    if (clinicId) {
      url += '&clinicId=' + clinicId;
    }
    if (groupId) {
      url += '&groupId=' + groupId;
    }
    if (envFromUrl) {
      url += '&env=' + envFromUrl;
    }
    const response = await api.post(url, data);
    return response;
  } catch (error) {
    console.error('Failed to save questionnaire:', error);
    throw error;
  }
};

const fetchFeedback = async (data, language, version) => {
  try {
    let url = '/?route=feedback&lang=' + language;
    if (version) {
      url += '&version=' + version;
    }
    if (envFromUrl) {
      url += '&env=' + envFromUrl;
    }
    const response = await api.post(url, data);
    return response;
  } catch (error) {
    console.error('Failed to submit feedback:', error);
    throw error;
  }
};

const generateActivationLink = async (clientId, professionalId) => {
  try {
    let url = '/?route=link/activate';
    if (envFromUrl) {
      url += '&env=' + envFromUrl;
    }
    const response = await api.post(url, {
      clientId,
      professionalId,
    });
    return response;
  } catch (error) {
    console.error('Failed to generate activation link:', error);
    throw error;
  }
};

const deactivateLink = async (clientId, professionalId) => {
  try {
    let url = '/?route=link/deactivate';
    if (envFromUrl) {
      url += '&env=' + envFromUrl;
    }
    const response = await api.post(url, {
      clientId,
      professionalId,
    });
    return response;
  } catch (error) {
    console.error('Failed to deactivate link:', error);
    throw error;
  }
};

const deleteLink = async (clientId, professionalId) => {
  try {
    let url = '/?route=link/delete';
    if (envFromUrl) {
      url += '&env=' + envFromUrl;
    }
    const response = await api.post(url, {
      clientId,
      professionalId,
    });
    return response;
  } catch (error) {
    console.error('Failed to delete link:', error);
    throw error;
  }
};

const getGeneratedLink = async (clientId, professionalId) => {
  try {
    let url = '/?route=link/get';
    if (envFromUrl) {
      url += '&env=' + envFromUrl;
    }
    const response = await api.get(url, {
      params: { clientId, professionalId },
    });
    return response;
  } catch (error) {
    console.error('Failed to get generated link:', error);
    throw error;
  }
};

const getLinks = async ({ professionalId, offset = 0, limit = 25 }) => {
  try {
    let url = '/?route=links';
    url += `&offset=${offset}&limit=${limit}`;
    if (professionalId) url += `&professionalId=${professionalId}`;
    if (envFromUrl) {
      url += '&env=' + envFromUrl;
    }
    const response = await api.get(url);
    return response;
  } catch (error) {
    console.error('Failed to fetch links:', error);
    throw error;
  }
};

export {
  fetchQuestions,
  fetchScores,
  saveQuestionnaire,
  fetchFeedback,
  generateActivationLink,
  getGeneratedLink,
  deactivateLink,
  deleteLink,
  getLinks,
};
